import type { ShowConsultation } from '@/components/PatientPageV2/components/ConsultationTabSections/ShowConsultation';
import { PrescriptionAssistantModal } from '@/shared-ui/PrescriptionAssistantModal/PrescriptionAssistantModal';
import { useAppStore } from '@/state-management';
import { Typography } from '@montugroup/design-system';
import { useCallback, useState } from 'react';
import { useSaveScheduledConsult } from '../../ScheduledConsult/hooks/useSaveScheduledConsult';
import { useScheduledConsult } from '../../ScheduledConsult/hooks/useScheduledConsult';
import { useScheduledConsultId } from '../../ScheduledConsult/hooks/useScheduledConsultId';
import { usePromptOnUnload } from './hooks/usePromptOnUnload';

interface UnsavedScheduledConsultPrompt {
  doctorId: number;
}

export const UnsavedScheduledConsultPrompt = ({ doctorId }: UnsavedScheduledConsultPrompt) => {
  const store = useAppStore();
  const notes = store.guidedDoctorConsult.fields.notes;
  const hasLocalChanges = store.guidedDoctorConsult.isDirty;
  const [showPrompt, setShowPrompt] = useState(false);
  const patientId = store.getPatientId();
  const scheduledConsultId = useScheduledConsultId();

  const { scheduledConsult } = useScheduledConsult({ patientId: patientId ?? undefined, scheduledConsultId });

  const { saveConsult, savingConsult } = useSaveScheduledConsult({
    doctorId,
    consultation: (scheduledConsult as ShowConsultation | undefined) ?? null,
  });

  const onConfirm = async () => {
    if (scheduledConsult) {
      await saveConsult(
        {
          id: scheduledConsult.id,
          notes: notes ?? '',
          status_id: scheduledConsult.status_id ?? 0,
          sub_status_id: scheduledConsult.sub_status_id ?? 0,
        },
        {
          askConfirmation: false,
        },
      );
    }

    setShowPrompt(false);
  };

  const onCancel = () => {
    setShowPrompt(false);
  };

  const onPromptShown = useCallback(() => {
    setShowPrompt(true);
  }, [setShowPrompt]);

  usePromptOnUnload({ showPrompt: hasLocalChanges, onPromptShown });

  return (
    <PrescriptionAssistantModal
      open={showPrompt}
      title="Save Changes?"
      body={
        <Typography paragraph>
          Changes made during this consult haven&rsquo;t been saved. Click <b>Save Changes</b> to keep your updates, or{' '}
          <b>Cancel</b> to stay on this page.
        </Typography>
      }
      confirmLabel="Save Changes"
      onConfirm={onConfirm}
      onClose={onCancel}
      isLoading={savingConsult}
    />
  );
};
